import axios from "axios";
import { getCookie } from "../utils/cookieUtils";

const bankAccountsApiBaseUrl: string = import.meta.env.VITE_BANK_ACCOUNTS_API;

const bankAccountsApi = axios.create({
  baseURL: bankAccountsApiBaseUrl,
});

bankAccountsApi.interceptors.request.use((config) => {
  const token = getCookie("authToken");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export interface BankAccount {
  id: string;
  user_id: string;
  bank: string;
  account: string;
  clabe: string;
  account_type: string;
  name: string; // New field for account owner's name
  created_at: string;
  updated_at: string;
}

export interface CreateBankAccountRequest {
  user_id: string;
  bank: string;
  account: string;
  clabe: string;
  account_type: string;
  name: string; // New field for account owner's name
}

export interface UpdateBankAccountRequest {
  id: string;
  bank?: string;
  account?: string;
  clabe?: string;
  account_type?: string;
  name?: string; // New field (optional) for updating the account owner's name
}

export interface ListBankAccountsResponse {
  bank_accounts: BankAccount[];
  next_page_token?: string;
}

interface ListBankAccountsQueryParams {
  user_id: string;
  limit: number;
  start_after?: string;
  bank?: string;
}

export const createBankAccount = async (
  bankAccount: CreateBankAccountRequest
): Promise<BankAccount> => {
  const response = await bankAccountsApi.post<BankAccount>(
    "/api/bankaccount",
    bankAccount
  );
  return response.data;
};

export const getBankAccount = async (id: string): Promise<BankAccount> => {
  const response = await bankAccountsApi.get<BankAccount>("/api/bankaccount", {
    params: { id },
  });
  return response.data;
};

export const listBankAccounts = async (
  userId: string,
  limit = 10,
  startAfter?: string,
  bank?: string
): Promise<ListBankAccountsResponse> => {
  const params: ListBankAccountsQueryParams = {
    user_id: userId,
    limit,
    start_after: startAfter,
  };

  if (bank) {
    params.bank = bank;
  }

  const response = await bankAccountsApi.get<ListBankAccountsResponse>(
    "/api/bankaccount/list",
    { params }
  );
  return response.data;
};

export const updateBankAccount = async (
  update: UpdateBankAccountRequest
): Promise<void> => {
  await bankAccountsApi.put("/api/bankaccount", update);
};

export const deleteBankAccount = async (id: string): Promise<void> => {
  await bankAccountsApi.delete("/api/bankaccount", {
    params: { id },
  });
};
